import { useEffect, type FunctionComponent } from "react";
import { Alert, DatePicker, Form, Modal, Radio, type FormInstance } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

interface FormModel {
  original_access_date: null | Dayjs;
  resumes_at: null | Dayjs;
  extensionType: "freePeriodAfterEnd" | "pauseCollection";
}

interface AccessPeriodModalProps {
  open: boolean;
  onCancel: () => void;
  onOk: () => void;
  loading: boolean;
  form: FormInstance<FormModel>;
  onFinish: (formData: FormModel) => Promise<void>;
  isInstallments: boolean;
  /**
   * When access really ends, packages already queued up included. Both the
   * read-only "access to" field and the +1/+3/+12 shortcuts measure from here —
   * anchoring them on `accessPeriod` alone offered dates that fall INSIDE a
   * package the client already paid for, and saving one of those cut it short
   * (TRE-1778).
   */
  accessEnd: Dayjs | null;
  getDisabledDate: (current: Dayjs) => boolean;
  defaultValue: Dayjs | null;
  blockPauseCollection: boolean;
}

const AccessPeriodModal: FunctionComponent<AccessPeriodModalProps> = ({
  open,
  onCancel,
  onOk,
  loading,
  form,
  onFinish,
  isInstallments,
  accessEnd,
  getDisabledDate,
  defaultValue,
}) => {
  const { t } = useTranslation(["client", "products", "common"]);

  // The parent owns the `FormInstance` and this modal is never unmounted, so
  // `initialValues` runs once for the lifetime of the page: on every reopen the form
  // still holds the date picked the previous time, and "access to" shows a horizon
  // from before the last save. Re-seed it whenever the modal opens — an empty date
  // field is the whole point of not pre-filling it (TRE-1778).
  const accessEndAt = accessEnd?.unix() ?? null;

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      extensionType: "pauseCollection",
      original_access_date: accessEndAt ? dayjs.unix(accessEndAt) : null,
      resumes_at: null,
    });
  }, [open, accessEndAt, form]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={isInstallments ? t("pauseCollection.extendAccessPeriod") : t("pauseCollection.pausePaymentCollection")}
      width={600}
      okText={t("common:button.save")}
      onOk={onOk}
      okButtonProps={{
        loading,
      }}
    >
      <Alert showIcon message={t("pauseCollection.info")} style={{ marginBottom: 16 }} />
      <Form<FormModel>
        form={form}
        labelWrap
        labelCol={{ xxl: { span: 8, offset: 2 }, span: 24, offset: 0 }}
        wrapperCol={{ xxl: { span: 16, offset: 2 }, span: 24, offset: 0 }}
        onFinish={onFinish}
        initialValues={{
          extensionType: "pauseCollection",
          original_access_date: accessEnd,
          // Empty on purpose: pre-filled with the pause already in place, "open the
          // modal and confirm" silently wrote a date from before the current package
          // ends (TRE-1778). The new date is always a deliberate choice.
          resumes_at: null,
        }}
      >
        <Form.Item
          name="extensionType"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          hidden
          label={t("pauseCollection.extensionType")}
        >
          <Radio.Group>
            <Radio.Button value="freePeriodAfterEnd">{t("pauseCollection.freePeriodAfterEnd")}</Radio.Button>
            <Radio.Button value="pauseCollection">{t("pauseCollection.pauseCollection")}</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="original_access_date" label={t("pauseCollection.accessTo")}>
          <DatePicker disabled style={{ minWidth: 250 }} />
        </Form.Item>
        <Form.Item
          name="resumes_at"
          label={isInstallments ? t("pauseCollection.extendTo") : t("pauseCollection.pauseTo")}
        >
          <DatePicker
            style={{ minWidth: 250 }}
            disabledDate={getDisabledDate}
            defaultPickerValue={defaultValue ?? undefined}
            showNow={false}
          />
        </Form.Item>
      </Form>
      <div style={{ textAlign: "center", marginTop: 10 }}>
        <Radio.Group
          value={null}
          onChange={(e) =>
            form.setFieldsValue({
              resumes_at: accessEnd?.add(Number(e.target.value), "month"),
            })
          }
        >
          <Radio.Button value={1}>{t("pauseCollection.extendAbout1Month")}</Radio.Button>
          <Radio.Button value={3}>{t("pauseCollection.extendAbout3Month")}</Radio.Button>
          <Radio.Button value={12}>{t("pauseCollection.extendAbout12Month")}</Radio.Button>
        </Radio.Group>
      </div>
    </Modal>
  );
};

export default AccessPeriodModal;
